import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from 'antd';
import { APIS, CommonResultListSearchItemCondition } from '@src/services';
import { SFCAvatar } from '@features/common';
import { MentionsInput, Mention, MentionItem, SuggestionDataItem } from 'react-mentions';
import throttle from 'lodash.throttle';
import './Comment.less';

export interface ICommentProps {
  level?: number;
  height: number;
  maxWidth?: number;
  backgroundColor?: string;
  commentType?: number; //评论类型 1.主评论 2.子评论（回复主评论的评论） 3 评论子评论的评论
  commentId?: number | string; // 回复评论id(评论类型为子评论时不能为空)
  respondent?: string; // 回复的评论人(评论类型为子评论时不能为空)
  onSubmit: (
    text: string,
    users: string[],
    commentType?: number,
    commentId?: number | string,
    respondent?: string
  ) => void;
  onRef?: (input: HTMLInputElement) => void;
  type?: 'big';
  className?: string;
}
type Props = ICommentProps;

interface SuggestUserInfoItem extends SuggestionDataItem {
  avatar?: string;
  aliasName?: string;
  name?: string;
}

export function Comment(props: Readonly<Props>) {
  const [value, changeValue] = useState('');
  const [plainTextValue, changePlainTextValue] = useState('');
  const [mentions, changeMentions] = useState<MentionItem[]>([]);
  const input = useRef<HTMLInputElement>(null);
  const {
    onRef,
    commentType,
    commentId,
    respondent,
    type,
    height,
    onSubmit,
    backgroundColor = '#f7f8f9',
    className = ''
  } = props;

  const displayTransform = (id: string, display: string) => `@${display}`;

  useEffect(() => {
    if (input.current && onRef) {
      onRef(input.current);
    }
  }, [onRef]);

  const onInputChange = (
    event: { target: { value: string } },
    newValue: string,
    newPlainTextValue: string,
    mentions: MentionItem[]
  ) => {
    changeValue(newValue.replace(/(\s{0,1}(@)(?!\[))/g, ' $2'));
    changeMentions(mentions);
    changePlainTextValue(newPlainTextValue);
  };

  const submit = () => {
    const text = plainTextValue.trim();

    if (!text) {
      return;
    }

    onSubmit(
      text,
      mentions.map(m => m.id),
      commentType,
      commentId,
      respondent
    );
    changeValue('');
    changeMentions([]);
    changePlainTextValue('');
  };

  const searchUser = (query: string, callback: (data: SuggestUserInfoItem[]) => void) => {
    const q = query.trim();
    if (!q) {
      return callback([]);
    }
    APIS.SearchApi.searchPageTabUsingGet({ keyWord: query, type: 'USER' })
      .then((res: CommonResultListSearchItemCondition) => {
        if (res.code === 0) {
          callback(
            (res.result &&
              res.result.map(u => ({
                id: u.username as string,
                display: u.name as string,
                name: u.name,
                avatar: u.photo
              }))) ||
              []
          );
        } else {
          callback([]);
        }
      })
      .catch(() => callback([]));
  };

  const search = useCallback(throttle(searchUser, 700), []);

  return (
    <div
      className={`${className} common-presenter k-common-comment ${
        type === 'big' ? 'comment__big' : ''
      }`}
    >
      <MentionsInput
        className="comment-mentions-input"
        placeholder="快来发表评论吧"
        onChange={onInputChange}
        value={value}
        inputRef={input}
        style={{
          suggestions: {
            list: {
              overflow: 'auto',
              position: 'absolute',
              width: 240,
              backgroundColor: '#fff',
              boxShadow: '0 3px 20px 0 rgba(0, 0, 0, 0.2)',
              borderRadius: 6,
              padding: '8px 0',
              bottom: 14
            }
          },
          input: {
            minHeight: height,
            padding: '7px',
            border: 'none',
            outline: 'none',
            resize: 'none',
            backgroundColor,
            borderRadius: '8px',
            paddingRight: type === 'big' ? '7px' : '84px'
          }
        }}
      >
        <Mention
          trigger="@"
          displayTransform={displayTransform}
          data={search}
          renderSuggestion={(suggestion: SuggestUserInfoItem) => (
            <div className="ksatck-base-mention-item">
              <SFCAvatar className="mention-img" src={suggestion.avatar} />
              {suggestion.display}
            </div>
          )}
          appendSpaceOnAdd
        />
      </MentionsInput>
      <Button
        type={type === 'big' ? 'primary' : 'default'}
        size={type === 'big' ? 'default' : 'small'}
        onClick={() => submit()}
        className={type === 'big' ? 'btn__big' : 'btn__small'}
      >
        发布
      </Button>
    </div>
  );
}

export default Comment;
